import { useMemo } from 'react';
import type { CurrentRead } from '@signalvote/data';
import { useFinanceHealth } from '@/hooks/queries/useFinanceDeadlines';
import { periodLabel } from '@/lib/finance/periodLabels';

/** The owed report's calendar period window, used to scope the close page. */
export interface OwedReportWindow {
  /** ISO yyyy-MM-dd start of the owed report's period (inclusive). */
  start: string;
  /** ISO yyyy-MM-dd end of the owed report's period (inclusive). */
  end: string;
  /** Report name, e.g. "June Monthly Report". */
  name: string;
  /** ISO date string when the report is due (for urgency calculation). */
  dueDate: string;
  /**
   * Cadence-agnostic label for the period, e.g. "June 2026" (AL monthly),
   * "Apr–Jun 2026" (FEC quarterly), "Oct 15–21, 2026" (AL weekly). Derived from
   * the period bounds, never the start month alone — see `periodLabel`.
   */
  periodLabel: string;
}

/**
 * The owed report's period window as a read. Ready with `null` means the treasurer is caught up
 * (no owed report) or the owed report carries no period bounds; the close page then falls back to
 * its calendar-open-period behavior. A failed, offline or loading health read passes through as
 * itself: the owed report is unknown, and the calendar period must not stand in for it.
 *
 * The owed report is `health.reportToActOn`, the earliest unfiled report that has
 * treasurer-entered activity. This is the single source of truth for the close page's period
 * scoping: pips, ledger grouping, footer stats, and the File gate all read this same window so
 * they cannot drift.
 */
export function useOwedReportWindow(): CurrentRead<OwedReportWindow | null> {
  const health = useFinanceHealth();
  const rta = health.status === 'ready' ? health.data.reportToActOn : null;
  // Read the owed report's primitive fields up front so the memo below keys on
  // scalars, not the health object identity.
  const start = rta?.periodStart ?? null;
  const end = rta?.periodEnd ?? null;
  const name = rta?.name ?? null;
  const dueDate = rta?.dueDate ?? null;
  // Root cause #1 of the close-ledger slowness: the window is a dependency of the
  // `renderRows` useMemo in `TriageLedger`. A fresh object literal every render
  // makes that memo miss its cache, rebuilding the entire bucketed row list on
  // EVERY render. Memoize on the primitive fields so the window keeps a stable
  // reference while the owed report is unchanged.
  const owedWindow = useMemo<OwedReportWindow | null>(() => {
    if (start == null || end == null || name == null || dueDate == null) {
      return null;
    }
    return {
      start,
      end,
      name,
      dueDate,
      periodLabel: periodLabel(start, end),
    };
  }, [start, end, name, dueDate]);
  return health.status === 'ready' ? { status: 'ready', data: owedWindow, refreshing: health.refreshing } : health;
}
